import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { FiChevronLeft } from "react-icons/fi";

import { RiskInfo } from "./RiskInfo";
import type { ResolvedClinicalCondition } from "../EosManager/resolve";
import "./EosResultsAccordion.scss";

import refreshIcon from "../../../assets/refreshCcw.svg";

type Resolved = {
  well: ResolvedClinicalCondition;
  equivocal: ResolvedClinicalCondition;
  illness: ResolvedClinicalCondition;
};

type Props = {
  eosPerClinicalCondition: Record<string, string | number>;
  resolved: Resolved;
  onOpenInfoModal: (id: 1 | 2 | 3 | 4) => void;
  onReset: () => void;
};

type SectionKey = "Well Appearing" | "Equivocal" | "Clinical Illness";

type Section = {
  key: SectionKey;
  color: "none" | "green" | "yellow" | "red";
  title: string;
  infoModalId: 1 | 2 | 3;
  recommendation: ResolvedClinicalCondition["recommendation"];
  riskValue: string | number;
};

//  stable test ids for e2e assertions
const riskTestIds: Record<SectionKey, string> = {
  "Well Appearing": "wellAppearingEos",
  Equivocal: "equivocalEos",
  "Clinical Illness": "clinicalIllnessEos",
};

export const EosResultsAccordion: React.FC<Props> = ({
  eosPerClinicalCondition,
  resolved,
  onOpenInfoModal,
  onReset,
}) => {
  const [openKey, setOpenKey] = React.useState<SectionKey | null>(null);

  const sections: Section[] = [
    {
      key: "Well Appearing",
      color: resolved.well.color,
      title: "אסימפטומטיים Well Appearing",
      infoModalId: 1,
      recommendation: resolved.well.recommendation,
      riskValue: eosPerClinicalCondition["Well Appearing"],
    },
    {
      key: "Equivocal",
      color: resolved.equivocal.color,
      title: "סימפטומים לא ספציפיים Equivocal",
      infoModalId: 2,
      recommendation: resolved.equivocal.recommendation,
      riskValue: eosPerClinicalCondition["Equivocal"],
    },
    {
      key: "Clinical Illness",
      color: resolved.illness.color,
      title: "סימנים משמעותיים Clinical Illness",
      infoModalId: 3,
      recommendation: resolved.illness.recommendation,
      riskValue: eosPerClinicalCondition["Clinical Illness"],
    },
  ];

  const toggle = (key: SectionKey) => {
    setOpenKey((prev) => (prev === key ? null : key));
  };

  return (
    <div className="eos-results" id="result" data-testid="eosResults">
      {/* Top info line */}
      <div className="eos-results__topline">
        <span>לפי ערך סיכון של 0.5/1000 EOS באוכלוסייה</span>
      </div>

      <div className="eos-result-card__title--bold">המשך טיפול</div>

      <div>עפ״י מצב סיפטומים אצל התינוק</div>

      <div className="eos-results__list">
        {sections.map((s) => {
          const isOpen = openKey === s.key;

          return (
            <section
              key={s.key}
              className={`eos-result-card eos-result-card--${s.color}`}
              aria-expanded={isOpen}
              //  optional section-level test id
              data-testid={`eosSection-${s.key.replace(/\s+/g, "")}`}
            >
              {/* Header row */}
              <div className="eos-result-card__header">
                <div className="eos-result-card__title">{s.title}</div>

                <button
                  type="button"
                  className="eos-result-card__infoBtn"
                  aria-label="Info"
                  onClick={() => onOpenInfoModal(s.infoModalId)}
                >
                  <FontAwesomeIcon
                    icon={faCircleInfo}
                    style={{ cursor: "pointer" }}
                  />
                </button>
              </div>

              <div className="eos-result-card__risk">
                סיכוי של{" "}
                <span
                  className="eos-result-card__riskValue"
                  data-testid={riskTestIds[s.key]}
                >
                  {s.riskValue}
                </span>
              </div>

              {/* ONE white card: header + content */}
              <div className="eos-result-card__whiteCard">
                <button
                  type="button"
                  className="eos-result-card__whiteCardHeader"
                  data-track-id="eos-result-card"
                  onClick={() => toggle(s.key)}
                  aria-expanded={isOpen}
                >
                  <span className="eos-result-card__toggleText">
                    המלצות טיפול
                  </span>

                  <FiChevronLeft
                    className={`eos-result-card__chevron ${
                      isOpen ? "is-open" : ""
                    }`}
                    aria-hidden="true"
                  />
                </button>

                {isOpen && (
                  <div className="eos-result-card__whiteCardBody">
                    <RiskInfo
                      recommendation={s.recommendation}
                      risk={s.riskValue}
                    />
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};